/* ==========================================================================
   FLC Forge — Collection Filter & Sort
   ========================================================================== */

/* Grid Position Memory (DEV-187)
   ==========================================================================
   Keeps the shopper's place when they go PLP → PDP → Back.

   Three parts:
   1. SAVE   — any click inside the product grid snapshots the rendered grid
               (all "Load More" pages), the scroll offset and the pagination
               state into sessionStorage, keyed by collection/search URL
               (filters + sort included, `page` excluded).
   2. RESTORE— on a back-navigation the snapshot is painted straight back in
               and the scroll offset re-applied, so the shopper lands on the
               exact card they left from with every loaded page still there.
   3. URL    — `?page=N` is kept in sync (replaceState) as pages load, so a
               refresh or a shared link still lands deep in the list. On such
               a load the earlier pages are back-filled above the current one.

   Disruptor tiles are positioned by `forloop.index` inside {% paginate %},
   so Liquid re-renders them on every page. They are only ever kept from
   page 1 — every other page contributes product cards only.
   ========================================================================== */
const GridMemory = {
  PREFIX: 'flc:gridmem:',
  TTL_MS: 30 * 60 * 1000,   // snapshots older than this are ignored
  MAX_BYTES: 3000000,       // stay well inside the ~5MB sessionStorage quota
  MAX_BACKFILL_PAGES: 9,    // safety cap on ?page=N deep-links

  userScrolled: false,

  /* ---- helpers ---------------------------------------------------------- */

  get main() { return document.querySelector('.collection-layout__main'); },
  get grid() { return document.querySelector('[data-product-grid]'); },

  /** sessionStorage key: path + sorted query string, minus paging noise */
  key(href = window.location.href) {
    const url    = new URL(href, window.location.origin);
    const params = new URLSearchParams(url.search);
    params.delete('page');
    params.delete('sections');
    const qs = [...params.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
    return `${this.PREFIX}${url.pathname}${qs ? '?' + qs : ''}`;
  },

  currentPage() {
    const p = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  },

  /** Build the URL for page N of the current view, preserving filters/sort */
  pageUrl(n) {
    const url = new URL(window.location.href);
    if (n > 1) url.searchParams.set('page', n);
    else url.searchParams.delete('page');
    return `${url.pathname}${url.search}`;
  },

  /** Keep ?page=N in the address bar without adding history entries */
  setPageParam(n) {
    const page = parseInt(n, 10);
    if (!Number.isFinite(page)) return;
    try {
      window.history.replaceState(window.history.state, '', this.pageUrl(page));
    } catch (err) { /* no-op */ }
  },

  headerOffset() {
    const header = document.querySelector('.site-header');
    return header ? header.offsetHeight + 12 : 0;
  },

  /* ---- history entry stamp ---------------------------------------------
     Browsers disagree on how a Back navigation is reported —
     `PerformanceNavigationTiming.type` is unreliable in WebKit and
     `document.referrer` is empty on Back everywhere — so instead of asking
     the browser HOW we got here, we mark the history entry itself on the way
     out. Only the entry we snapshotted carries the stamp: a fresh forward
     navigation to the same URL creates a new entry with no state, so it
     correctly gets a clean page.
     -------------------------------------------------------------------- */

  stamp() {
    try {
      const state = Object.assign({}, window.history.state || {});
      state.flcGrid = this.key();
      window.history.replaceState(state, '');
    } catch (err) { /* no-op */ }
  },

  isStamped() {
    try {
      return !!(window.history.state && window.history.state.flcGrid === this.key());
    } catch (err) {
      return false;
    }
  },

  isReload() {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type === 'reload';
      return !!(performance.navigation && performance.navigation.type === 1);
    } catch (err) {
      return false;
    }
  },

  /* ---- scrolling -------------------------------------------------------- */

  /**
   * The theme sets `html { scroll-behavior: smooth }`, which silently breaks
   * position restore: every scrollTo starts a fresh animation from wherever
   * the page currently is, so the rapid re-applies below cancel each other
   * out and the page never leaves the top. Pin `scroll-behavior: auto` on
   * <html> for the duration of the restore and hand it back afterwards.
   */
  beginInstantScroll() {
    if (this._prevScrollBehavior !== undefined) return;
    const html = document.documentElement;
    this._prevScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
  },

  endInstantScroll() {
    if (this._prevScrollBehavior === undefined) return;
    document.documentElement.style.scrollBehavior = this._prevScrollBehavior;
    this._prevScrollBehavior = undefined;
    if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
  },

  jumpTo(y) {
    try {
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    } catch (err) {
      window.scrollTo(0, y);
    }
  },

  /* ---- save ------------------------------------------------------------- */

  /**
   * Serialise the grid with the wishlist app's injected markup stripped back
   * out. `.button-icon` / `.button-text` are empty in product-card.liquid and
   * filled in by the wishlist app at runtime — snapshotting its output would
   * double up the heart on restore, and would show a stale state if the
   * shopper wishlisted the item while they were on the PDP. Emptying them and
   * firing `initTheWishlist` on restore lets the app redraw from live state.
   */
  snapshotHtml(main) {
    const clone = main.cloneNode(true);
    clone.querySelectorAll('.button-collection-wishlist .button-icon, .button-collection-wishlist .button-text')
      .forEach(el => { el.innerHTML = ''; });
    clone.querySelectorAll('.is-loading').forEach(el => el.classList.remove('is-loading'));
    return clone.innerHTML;
  },

  /**
   * Describe the card the shopper is leaving through: where it sits in the
   * grid, and where it sat in the viewport.
   *
   * Positioning off this beats positioning off a saved pixel offset. The
   * offset has to be read at exactly the right moment and re-applied against
   * a page that has finished laying out — iOS Safari reliably breaks both
   * halves of that. An anchor can be re-measured on every retry, so it
   * self-corrects as the restored grid settles, and it survives `scrollY`
   * reading zero while the page is being torn down.
   */
  anchorFor(cardEl) {
    const grid = this.grid;
    if (!grid || !cardEl) return null;
    const index = [...grid.children].indexOf(cardEl);
    if (index < 0) return null;
    return {
      index,
      viewportTop: Math.round(cardEl.getBoundingClientRect().top),
      href: cardEl.querySelector('a[href]')?.getAttribute('href') || null
    };
  },

  save(anchor) {
    const main = this.main;
    if (!main) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const page    = this.currentPage();

    // Mark the history entry regardless of whether the snapshot itself fits —
    // the lightweight fallback below still needs to know this is a return.
    this.stamp();

    if (anchor) this._savedAnchor = true;

    try {
      const payload = JSON.stringify({
        html: this.snapshotHtml(main),
        scrollY,
        anchor: anchor || null,
        page,
        ts: Date.now()
      });
      if (payload.length > this.MAX_BYTES) { this.saveLight(page, scrollY, anchor); return; }
      sessionStorage.setItem(this.key(), payload);
    } catch (err) {
      // Quota exceeded, or Safari private browsing where the quota is zero.
      // Fall back to remembering just the position and rebuilding on return.
      this.saveLight(page, scrollY, anchor);
    }
  },

  /**
   * Fallback for when the full snapshot won't fit: store only how deep the
   * shopper was. On return the pages are re-fetched and the position re-applied
   * — slower than a repaint, but it still puts them back where they were.
   */
  saveLight(page, scrollY, anchor) {
    try {
      sessionStorage.setItem(this.key(), JSON.stringify({
        light: true, page, scrollY, anchor: anchor || null, ts: Date.now()
      }));
    } catch (err) {
      this.clear();
    }
  },

  read() {
    try {
      const raw = sessionStorage.getItem(this.key());
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || (!data.html && !data.light)) return null;
      if (Date.now() - (data.ts || 0) > this.TTL_MS) { this.clear(); return null; }
      return data;
    } catch (err) {
      return null;
    }
  },

  clear() {
    try { sessionStorage.removeItem(this.key()); } catch (err) { /* no-op */ }
  },

  /* ---- restore ---------------------------------------------------------- */

  /**
   * True when we're arriving back on the history entry we snapshotted.
   *
   * The stamp is the reliable signal and works identically in every browser.
   * The navigation-type check is kept only as a secondary path for the case
   * where replaceState was unavailable when we saved.
   */
  isReturnVisit() {
    if (this.isReload()) return false;
    if (this.isStamped()) return true;

    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type === 'back_forward') return true;
      if (performance.navigation && performance.navigation.type === 2) return true;
    } catch (err) { /* no-op */ }

    return false;
  },

  restore(data) {
    const main = this.main;
    if (!main) return false;

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    main.innerHTML = data.html;
    document.body.dispatchEvent(new CustomEvent('initTheWishlist'));
    this.restorePosition(data);
    return true;
  },

  /**
   * Work out where the page needs to be, from the anchor if we have one.
   * Re-measured on every attempt so a grid that lays out late still lands
   * correctly. Falls back to the saved pixel offset.
   */
  targetOffset(data) {
    const grid = this.grid;
    if (data && data.anchor && grid) {
      const el = grid.children[data.anchor.index];
      if (el && el.getBoundingClientRect) {
        const rect = el.getBoundingClientRect();
        // Ignore a not-yet-laid-out element (everything collapsed at zero).
        if (rect.height > 0 || rect.top !== 0) {
          const y = rect.top + (window.scrollY || 0) - data.anchor.viewportTop;
          return Math.max(0, Math.round(y));
        }
      }
    }
    return data ? (data.scrollY || 0) : 0;
  },

  /**
   * Re-apply a scroll offset across the async settling of the page
   * (custom-element upgrades, fonts, lazy images) — but bail out the moment
   * the shopper scrolls themselves, so we never yank the viewport from under
   * them.
   */
  restorePosition(data) {
    if (!data || (!data.anchor && !data.scrollY)) {
      this.endInstantScroll();
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
      return;
    }

    this.beginInstantScroll();

    const jump = () => {
      if (this.userScrolled) return;
      const y = this.targetOffset(data);
      if (y) this.jumpTo(y);
    };

    jump();
    requestAnimationFrame(() => { jump(); requestAnimationFrame(jump); });
    window.addEventListener('load', jump, { once: true });
    // iOS Safari lays the restored grid out and applies its own scroll
    // positioning noticeably later than Blink/Gecko, so keep correcting for
    // a while. Each pass re-measures the anchor, so a late reflow is absorbed
    // rather than leaving the shopper at the wrong offset.
    [100, 250, 500, 800, 1200, 1600].forEach(ms => setTimeout(jump, ms));
    setTimeout(() => { jump(); this.endInstantScroll(); }, 2000);
  },

  /** Back-compat shim — some call sites still think in raw offsets. */
  scrollToOffset(y) {
    this.restorePosition({ scrollY: y });
  },

  /* ---- back-fill: ?page=N deep links, and the lightweight restore -------- */

  async backfill(restoreData) {
    const page = this.currentPage();
    const grid = this.grid;
    if (!grid) return false;
    if (page < 2) {
      if (restoreData) this.restorePosition(restoreData);
      return false;
    }

    const firstPage = Math.max(1, page - this.MAX_BACKFILL_PAGES);
    // The first server-rendered card is where page N starts — scroll there
    // once the earlier pages have been inserted above it.
    const anchor = grid.querySelector('[data-product-card]');
    // Liquid re-renders disruptor tiles on every page; only page 1's are real.
    grid.querySelectorAll('.product-grid__disruptor').forEach(tile => tile.remove());
    grid.classList.add('is-loading');
    this.beginInstantScroll();

    const urls = [];
    for (let p = firstPage; p < page; p++) urls.push(this.pageUrl(p));

    try {
      const docs = await Promise.all(urls.map(async (url) => {
        const res = await fetch(url);
        return new DOMParser().parseFromString(await res.text(), 'text/html');
      }));

      const fragment = document.createDocumentFragment();
      docs.forEach((doc, i) => {
        const isFirstPage = (firstPage + i) === 1;
        const source = doc.querySelector('[data-product-grid]');
        if (!source) return;
        [...source.children].forEach(node => {
          const isCard      = node.matches('[data-product-card]');
          const isDisruptor = node.matches('.product-grid__disruptor');
          if (isCard || (isFirstPage && isDisruptor)) fragment.appendChild(node);
        });
      });

      if (fragment.childNodes.length) {
        grid.insertBefore(fragment, grid.firstChild);
        document.body.dispatchEvent(new CustomEvent('initTheWishlist'));
      }

      if (restoreData) {
        // Returning shopper: put them back on the card they left from.
        grid.classList.remove('is-loading');
        this.restorePosition(restoreData);
        return true;
      }

      if (anchor && !this.userScrolled) {
        const top = anchor.getBoundingClientRect().top + window.scrollY - this.headerOffset();
        this.jumpTo(Math.max(0, top));
      }
    } catch (err) {
      console.error('[GridMemory] back-fill failed:', err);
    } finally {
      grid.classList.remove('is-loading');
      if (!restoreData) this.endInstantScroll();
    }
    return true;
  },

  /* ---- wiring ----------------------------------------------------------- */

  init() {
    if (!this.main) return;

    // scrollRestoration is deliberately NOT forced to 'manual' up front —
    // leaving it pinned across the whole page life has been reported to jam
    // iOS Safari's swipe-to-go-back gesture. It's switched on only while a
    // restore is actually in flight, and handed straight back afterwards.

    // Deliberately NOT `touchstart`: on a touch device the shopper's finger
    // lands on the page during the back-swipe and while the restore is still
    // settling, which would abort it before it ever positioned. A touch that
    // turns into an actual drag (`touchmove`) is a real takeover; a tap is not.
    ['wheel', 'touchmove', 'keydown'].forEach(evt => {
      window.addEventListener(evt, () => { this.userScrolled = true; }, { passive: true, once: true });
    });

    // 1) SAVE — capture phase so it also fires for the product-card carousel,
    //    which navigates programmatically instead of following an <a href>.
    document.addEventListener('click', (e) => {
      const grid = this.grid;
      if (!grid || !grid.contains(e.target)) return;
      // Card-internal controls don't navigate away — skip the snapshot.
      if (e.target.closest('[data-pc-prev], [data-pc-next], .button-collection-wishlist, [data-quick-add]')) return;
      this.save(this.anchorFor(e.target.closest('[data-product-card]')));
    }, true);

    // Catch-all for any other way of leaving the page (keyboard, gestures).
    // Skipped once a click has already captured an anchor: `scrollY` is not
    // dependable while a page is being torn down (iOS Safari can report 0),
    // and re-saving here would overwrite a good snapshot with a useless one.
    window.addEventListener('pagehide', () => {
      if (this._savedAnchor) return;
      if (this.currentPage() > 1 || (window.scrollY || 0) > 0) this.save();
    });

    // 2) RESTORE — bfcache returns the live DOM already, nothing to do.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted && 'scrollRestoration' in history) history.scrollRestoration = 'auto';
    });

    const snapshot = this.isReturnVisit() ? this.read() : null;
    if (snapshot && snapshot.html) {
      this.restore(snapshot);
      return;
    }
    if (snapshot && snapshot.light) {
      // Snapshot didn't fit in storage — rebuild the pages, then reposition.
      this.backfill(snapshot);
      return;
    }

    // 3) Deep link / refresh on ?page=N — rebuild the pages above it.
    if (this.currentPage() > 1) {
      this.backfill();
      return;
    }

    if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
  }
};

window.FLCGridMemory = GridMemory;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => GridMemory.init());
} else {
  GridMemory.init();
}

class FilterDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer    = this.querySelector('[data-filter-inner]');
    this.openBtn   = document.querySelector('[data-filter-open]');
    this.closeBtn  = this.querySelector('[data-filter-close]');
    this.clearBtn  = this.querySelector('[data-filter-clear]');
    this.applyBtn  = this.querySelector('[data-filter-apply]');
    this.form      = this.querySelector('[data-filter-form]') || document.querySelector('[data-filter-form]');
    this.summary   = document.querySelector('[data-active-filters]');

    this.openBtn?.addEventListener('click',  () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());

    // Clear: uncheck all inputs then fetch the unfiltered collection
    this.clearBtn?.addEventListener('click', () => this.clearFilters());

    // Apply: submit current selections then close
    this.applyBtn?.addEventListener('click', async () => {
      await this.submitFilter();
      this.close();
    });

    document.addEventListener('flc:drawers:close-all', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    // Auto-apply while drawer is open so the count updates in real time
    this.form?.addEventListener('change', FLC.debounce(() => this.submitFilter(), 400));
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    this.drawer?.classList.add('is-open');
    FLC.openOverlay();
    FLC.trapFocus(this);
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.drawer?.classList.remove('is-open');
    FLC.closeOverlay();
  }

  clearFilters() {
    // Uncheck every checkbox in the form
    this.form?.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
    // Clear price range inputs so they are omitted from the params
    this.form?.querySelectorAll('input[type="number"]').forEach(n => { n.value = ''; });
    this.submitFilter();
  }

  async submitFilter() {
    const searchParams = new URLSearchParams(new FormData(this.form)).toString();
    const url = `${window.location.pathname}?${searchParams}`;
    this.setLoading(true);

    try {
      const res  = await fetch(`${url}&sections=main-collection`);
      const data = await res.json();

      if (data['main-collection']) {
        const parser = new DOMParser();
        const doc    = parser.parseFromString(data['main-collection'], 'text/html');
        this.replaceSection(doc);
        this.updateURL(url);
      }
    } catch (err) {
      console.error('Filter failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  replaceSection(doc) {
    // Replace the whole main area (grid classes, disruptor tiles, pagination).
    const newMain = doc.querySelector('.collection-layout__main');
    const curMain = document.querySelector('.collection-layout__main');
    if (newMain && curMain) {
      curMain.replaceWith(newMain);
      document.body.dispatchEvent(new CustomEvent('initTheWishlist'));
    }

    // Refresh the active filters summary + product count text.
    const newFilters = doc.querySelector('[data-active-filters]');
    if (newFilters && this.summary) this.summary.replaceWith(newFilters);
    const newCount = doc.querySelector('[data-product-count]');
    const curCount = document.querySelector('[data-product-count]');
    if (newCount && curCount) curCount.replaceWith(newCount);

    // Refresh the inline filter-bar (desktop) so its triggers stay in sync.
    const newBar = doc.querySelector('filter-bar');
    const curBar = document.querySelector('filter-bar');
    if (newBar && curBar) curBar.replaceWith(newBar);
  }

  updateURL(url) {
    // A new filter set means a new grid — drop the snapshot for the old view.
    GridMemory.clear();
    window.history.pushState({}, '', url);
  }

  setLoading(state) {
    document.querySelector('[data-product-grid]')?.classList.toggle('is-loading', state);
  }
}

customElements.define('filter-drawer', FilterDrawer);

/* Inline Filter Bar (collection page)
   ==========================================================================
   Top-bar layout with one dropdown panel per filter (image-2 design).
   Sort lives in a separate panel. Mobile (<750px) hides the bar and falls
   back to the existing <filter-drawer>.
*/
class FilterBar extends HTMLElement {
  connectedCallback() {
    this.form     = this.querySelector('[data-filter-form]');
    this.triggers = this.querySelectorAll('[data-filter-trigger]');
    this.panels   = this.querySelectorAll('[data-filter-panel]');

    this.triggers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.togglePanel(btn.dataset.filterTrigger);
      });
    });

    // Per-panel Clear / Apply buttons
    this.querySelectorAll('[data-filter-clear]').forEach(b =>
      b.addEventListener('click', () => this.clearFilters())
    );
    this.querySelectorAll('[data-filter-apply]').forEach(b =>
      b.addEventListener('click', () => { this.submitFilter(true); this.closeAll(); })
    );

    // Live count update on change (debounced) — does NOT close the panel
    if (this.form) {
      this.form.addEventListener('change', FLC.debounce(() => {
        this.syncTriggerStates();
        this.submitFilter(false);
      }, 300));
    }

    // Sort radios — apply via AJAX so wishlist icons are refreshed consistently
    this.querySelectorAll('[data-sort-value]').forEach(radio => {
      radio.addEventListener('change', () => {
        this.submitFilter(true);
        this.closeAll();
      });
    });

    // Click outside / Esc closes any open panel
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.closeAll();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAll();
    });
  }

  togglePanel(name) {
    const trigger = this.querySelector(`[data-filter-trigger="${name}"]`);
    const panel   = this.querySelector(`[data-filter-panel="${name}"]`);
    if (!panel) return;
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    this.closeAll();
    if (!isOpen) {
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    }
  }

  closeAll() {
    this.triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
    this.panels.forEach(p => { p.hidden = true; });
  }

  clearFilters() {
    if (!this.form) return;
    this.form.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
    this.form.querySelectorAll('input[type="number"]').forEach(n => { n.value = ''; });
    // Immediately clear all trigger active states so the UI responds
    // before the fetch resolves.
    this.syncTriggerStates();
    this.submitFilter(true);
  }

  // Recompute and apply is-active class + count badge on every trigger
  // based on the current form checkbox/input state.
  syncTriggerStates() {
    this.querySelectorAll('[data-filter-trigger]').forEach(trigger => {
      const panelName = trigger.dataset.filterTrigger;
      if (panelName === 'sort') return; // sort has no active state
      const panel = this.querySelector(`[data-filter-panel="${panelName}"]`);
      if (!panel) return;

      const checkedCount = panel.querySelectorAll('input[type="checkbox"]:checked').length;
      const hasPriceVal  = [...panel.querySelectorAll('input[type="number"]')]
                             .some(n => n.value !== '');
      const activeCount = checkedCount + (hasPriceVal ? 1 : 0);

      trigger.classList.toggle('is-active', activeCount > 0);

      // Update or remove the inline count badge
      let badge = trigger.querySelector('[data-filter-trigger-count]');
      if (activeCount > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'filter-bar__trigger-count';
          badge.setAttribute('data-filter-trigger-count', '');
          badge.setAttribute('aria-hidden', 'true');
          // Insert before the caret
          const caret = trigger.querySelector('.filter-bar__trigger-caret');
          trigger.insertBefore(badge, caret);
        }
        badge.textContent = `(${activeCount})`;
      } else if (badge) {
        badge.remove();
      }
    });
  }

  buildUrl() {
    const params = new URLSearchParams(new FormData(this.form));
    // Include sort from the checked radio (lives outside the <form>)
    const sortRadio = this.querySelector('[data-sort-value]:checked');
    if (sortRadio) params.set('sort_by', sortRadio.value);
    // Strip empty params so the URL stays clean
    [...params.entries()].forEach(([k, v]) => { if (!v) params.delete(k); });
    // Any filter/sort change resets paging back to the first page.
    params.delete('page');
    const qs = params.toString();
    return `${window.location.pathname}${qs ? '?' + qs : ''}`;
  }

  async submitFilter(replaceGrid) {
    if (!this.form) return;
    const url = this.buildUrl();
    if (replaceGrid) document.querySelector('[data-product-grid]')?.classList.add('is-loading');

    try {
      // Live count requests use the lightweight Sections API; "Apply" /
      // "Clear" use a full-page fetch so disruptor tile blocks, section
      // <style> blocks and grid classes come back exactly as Liquid would
      // render them on a fresh page load.
      const fetchUrl = replaceGrid
        ? url
        : `${url}${url.includes('?') ? '&' : '?'}sections=main-collection`;
      const res  = await fetch(fetchUrl);
      const text = await res.text();
      let doc;

      if (replaceGrid) {
        doc = new DOMParser().parseFromString(text, 'text/html');
      } else {
        const data = JSON.parse(text);
        if (!data['main-collection']) return;
        doc = new DOMParser().parseFromString(data['main-collection'], 'text/html');
      }

      // Always update the result count in any open Apply button
      const newCountEl = doc.querySelector('[data-product-count]');
      const newCount   = newCountEl ? newCountEl.textContent.trim().match(/\d+/)?.[0] : null;
      if (newCount) {
        this.querySelectorAll('[data-filter-apply-count]').forEach(el => { el.textContent = newCount; });
      }

      if (replaceGrid) {
        // 1) Replace the entire collection main area. This brings back the
        //    correct grid classes (columns, gaps), all disruptor tiles, and
        //    the pagination element in one shot.
        const newMain = doc.querySelector('.collection-layout__main');
        const curMain = document.querySelector('.collection-layout__main');
        if (newMain && curMain) {
          curMain.replaceWith(newMain);
          document.body.dispatchEvent(new CustomEvent('initTheWishlist'));
        }

        // 2) Replace the entire <filter-bar>. Liquid is the source of truth
        //    for trigger active-state classes, count badges, panel checked
        //    inputs, the active-filters tag list and the data-product-count.
        const newBar = doc.querySelector('filter-bar');
        if (newBar) this.replaceWith(newBar);

        // A new filter set means a new grid — drop the snapshot for the old view.
        GridMemory.clear();
        window.history.pushState({}, '', url);
      }
    } catch (err) {
      console.error('Filter bar fetch failed:', err);
    } finally {
      document.querySelector('[data-product-grid]')?.classList.remove('is-loading');
    }
  }
}

customElements.define('filter-bar', FilterBar);

/* Sort
   ========================================================================== */
const sortSelect = document.querySelector('[data-sort-select]');
if (sortSelect) {
  sortSelect.addEventListener('change', () => {
    const filterBar = document.querySelector('filter-bar');
    if (filterBar) {
      // Sync the filter-bar sort radio so buildUrl() picks it up
      const matchingRadio = filterBar.querySelector(`[data-sort-value][value="${sortSelect.value}"]`);
      if (matchingRadio) matchingRadio.checked = true;
      filterBar.submitFilter(true);
    } else {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }
  });
}

/* Active filter removal
   ========================================================================== */
document.addEventListener('click', (e) => {
  const removeBtn = e.target.closest('[data-remove-filter]');
  if (!removeBtn) return;
  e.preventDefault();

  const url = new URL(removeBtn.href);
  window.location.href = url.toString();
});

/* Infinite scroll
   ========================================================================== */
class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.nextUrl = this.dataset.nextUrl;
    if (!this.nextUrl) return;

    this.grid    = document.querySelector('[data-product-grid]');
    this.trigger = this;
    this.loading = false;

    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && this.nextUrl && !this.loading) this.loadMore();
    }, { rootMargin: '200px' });

    this.observer.observe(this.trigger);
  }

  async loadMore() {
    const url = this.nextUrl;
    this.nextUrl = null; // prevent double loading
    this.loading = true;
    this.classList.add('is-loading');

    try {
      const res  = await fetch(url);
      const text = await res.text();
      const parser   = new DOMParser();
      const doc      = parser.parseFromString(text, 'text/html');
      const newItems = doc.querySelectorAll('[data-product-card]:not(.product-grid__disruptor)');
      newItems.forEach(item => this.grid?.appendChild(item));
      if (newItems.length) document.body.dispatchEvent(new CustomEvent('initTheWishlist'));

      const nextTrigger = doc.querySelector('infinite-scroll');
      this.nextUrl = nextTrigger?.dataset.nextUrl || null;

      // Keep ?page=N in the address bar + keep the element self-describing so
      // a restored snapshot resumes from the right page (DEV-187).
      const loadedPage = new URL(url, window.location.origin).searchParams.get('page');
      if (loadedPage) GridMemory.setPageParam(loadedPage);
      this.dataset.nextUrl = this.nextUrl || '';
    } catch (err) {
      console.error('Infinite scroll failed:', err);
    } finally {
      this.loading = false;
      this.classList.remove('is-loading');
      if (this.nextUrl) {
        // Re-observe to reset intersection state — re-triggers loadMore
        // if the sentinel is still in the viewport after appending new items.
        this.observer.unobserve(this.trigger);
        this.observer.observe(this.trigger);
      } else {
        // No more pages — remove the sentinel element entirely.
        this.remove();
      }
    }
  }
}

customElements.define('infinite-scroll', InfiniteScroll);

/* Load More Button
   ==========================================================================
   DEV-199 — the button now loads the next pages by itself.

   The ask was for the grid to "scroll endlessly rather than stop at Load
   More". Switching the section to `pagination_type: infinite` would have done
   that in one setting, but it takes two things with it: the footer stops
   being reachable on the bigger collections (New Products is 468 products,
   16 pages), and a shopper accumulates pages passively — past roughly 400
   cards the DEV-187 back-navigation snapshot no longer fits in
   sessionStorage and Back silently drops to the slower re-fetch path, which
   only rebuilds 9 pages.

   So: the next LOAD_MORE_AUTO_PAGES pages load on their own as the shopper
   nears the bottom, and the button only reappears once that allowance is
   spent. Clicking it re-arms the allowance. The scroll feels continuous, the
   footer is still reachable, and DOM growth is still gated by a deliberate
   click every few pages.
   ========================================================================== */

/** Pages that load without a click before the button comes back. Can be
 *  overridden per section with `data-auto-pages` on <load-more-button>. */
const LOAD_MORE_AUTO_PAGES = 2;

/** How far ahead of the sentinel to start fetching — roughly one grid row,
 *  so the next cards are usually in place before the shopper reaches the end
 *  of the current ones. */
const LOAD_MORE_ROOT_MARGIN = '600px';

class LoadMoreButton extends HTMLElement {
  connectedCallback() {
    this.nextUrl  = this.dataset.nextUrl || null;
    this.viewed   = parseInt(this.dataset.viewed, 10);
    this.total    = parseInt(this.dataset.total, 10);
    this.grid     = document.querySelector('[data-product-grid]');
    this.btn      = this.querySelector('.load-more-btn');
    this.countEl  = this.querySelector('.load-more-count');
    this.loading  = false;

    const configured   = parseInt(this.dataset.autoPages, 10);
    this.autoPages     = Number.isFinite(configured) && configured >= 0 ? configured : LOAD_MORE_AUTO_PAGES;
    this.autoRemaining = this.autoPages;
    // Set when a fetch fails: automatic loading stops and the button comes
    // back, so a flaky connection ends in a retry the shopper can see rather
    // than a grid that quietly appears to end.
    this.autoFailed = false;
    this.inView     = false;

    // A click is a fresh mandate: it buys the next page AND another allowance
    // of automatic ones.
    this.btn?.addEventListener('click', () => {
      this.autoRemaining = this.autoPages;
      this.autoFailed    = false;
      this.loadMore();
    });

    this.initAutoLoad();
  }

  disconnectedCallback() {
    // The whole collection main area is replaced on filter/sort and on a
    // DEV-187 restore. Without this, the detached element keeps its scroll
    // listener and carries on appending pages into the grid that replaced it.
    this.observer?.disconnect();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }

  /* ---- automatic loading ------------------------------------------------ */

  initAutoLoad() {
    this.syncAutoState();
    if (!this.autoPages || typeof IntersectionObserver === 'undefined') return;

    this.observer = new IntersectionObserver((entries) => {
      this.inView = entries[0].isIntersecting;
      if (this.inView) this.maybeAutoLoad();
    }, { rootMargin: LOAD_MORE_ROOT_MARGIN });
    this.observer.observe(this);

    // An IntersectionObserver only reports CHANGES. On a tall screen the
    // element can already sit inside the root margin when the page loads —
    // where the shopper hasn't scrolled and we don't want to load anything.
    // Remember that it's in view and re-test on scroll instead.
    this.onScroll = () => { if (this.inView) this.maybeAutoLoad(); };
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  maybeAutoLoad() {
    if (this.loading || this.autoFailed) return;
    if (!this.nextUrl || this.autoRemaining <= 0) return;
    // Never auto-load a page the shopper hasn't scrolled towards.
    if ((window.scrollY || window.pageYOffset || 0) <= 0) return;
    this.autoRemaining -= 1;
    this.loadMore(true);
  }

  /** The button is shown only once there's nothing left to load on its own. */
  syncAutoState() {
    const armed = !!(this.nextUrl && this.autoRemaining > 0 && !this.autoFailed);
    this.classList.toggle('is-auto-loading', armed);
    // `hidden` isn't enough here: `.load-more-btn { display: block }` is an
    // author rule and beats the user-agent rule for [hidden].
    if (this.btn) this.btn.style.display = armed ? 'none' : '';
  }

  /** Spinner shown while a page loads without the button. Reused rather than
   *  recreated — a DEV-187 restore paints back whatever was in the DOM. */
  spinner() {
    let el = this.querySelector('.loading-spinner');
    if (!el) {
      el = document.createElement('div');
      el.className = 'loading-spinner';
      el.setAttribute('aria-hidden', 'true');
      this.appendChild(el);
    }
    return el;
  }

  /* ---- loading ----------------------------------------------------------- */

  async loadMore(auto = false) {
    if (this.loading || !this.nextUrl) return;
    const url = this.nextUrl;
    this.nextUrl = null;
    this.loading = true;
    this.setAttribute('aria-busy', 'true');

    if (auto) {
      this.spinner();
      // custom.css: `.is-loading .loading-spinner { display: inline-block }`
      this.classList.add('is-loading');
    }
    if (this.btn) {
      this.btn.disabled = true;
      this.btn.classList.add('is-loading');
    }

    try {
      const res  = await fetch(url);
      const text = await res.text();

      // The whole section can be replaced while this request is in flight —
      // a filter, a sort, or a DEV-187 restore. These cards belong to the
      // grid that has just been thrown away, so dropping them beats
      // appending page 2 of the previous result set to the new one. Much
      // easier to hit now that pages load without a click (DEV-199).
      if (!this.isConnected) return;

      const parser   = new DOMParser();
      const doc      = parser.parseFromString(text, 'text/html');
      const newItems = doc.querySelectorAll('[data-product-card]:not(.product-grid__disruptor)');
      const grid     = document.querySelector('[data-product-grid]');
      if (!grid) { console.error('[LoadMore] product grid not found in DOM'); return; }
      newItems.forEach(item => grid.appendChild(item));
      if (newItems.length) document.body.dispatchEvent(new CustomEvent('initTheWishlist'));
      this.viewed += newItems.length;

      const nextComp = doc.querySelector('load-more-button');
      this.nextUrl   = nextComp?.dataset.nextUrl || null;

      // If no new items were loaded (all filtered as swatch), hide button
      if (newItems.length === 0) {
        this.nextUrl = null;
      }

      // Keep ?page=N in the address bar so a refresh or a shared link still
      // lands on this page of results (DEV-187).
      const loadedPage = new URL(url, window.location.origin).searchParams.get('page');
      if (loadedPage) GridMemory.setPageParam(loadedPage);
    } catch (err) {
      console.error('Load more failed:', err);
      this.nextUrl = url; // restore so user can retry
      // Hand control back to the shopper rather than retrying by itself on
      // every scroll event (DEV-199).
      if (auto) this.autoFailed = true;
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
      this.classList.remove('is-loading');
      this.btn?.classList.remove('is-loading');

      if (this.nextUrl) {
        if (this.btn) this.btn.disabled = false;
      } else {
        this.btn?.remove();
        this.btn = null;
        this.querySelector('.loading-spinner')?.remove();
      }

      if (this.countEl) {
        const label = this.dataset.itemLabel || 'products';
        this.countEl.textContent = `You've viewed ${this.viewed} out of ${this.total} ${label}`;
      }

      // Mirror the live state back onto the element. A restored snapshot is
      // just HTML, so the dataset has to carry where the shopper got to
      // otherwise the next click would re-load page 2 (DEV-187).
      this.dataset.nextUrl = this.nextUrl || '';
      if (Number.isFinite(this.viewed)) this.dataset.viewed = this.viewed;

      // Show or hide the button for the state we've just landed in, then —
      // if the shopper is still sitting at the bottom and has allowance left
      // — reach for the next page straight away (DEV-199).
      this.syncAutoState();
      if (this.inView) this.maybeAutoLoad();
    }
  }
}

customElements.define('load-more-button', LoadMoreButton);
